import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown } from 'lucide-react';

export const LegalModal = ({ isOpen, onClose, type, faqData, legalNoticeData, cookiePolicyData }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  if (!isOpen) return null;

  const getContent = () => {
    if (type === 'faq') {
      return {
        title: 'Preguntas Frecuentes',
        isFaq: true
      };
    } else if (type === 'legal') {
      return legalNoticeData;
    } else if (type === 'cookies') {
      return cookiePolicyData;
    }
    return null;
  };

  const content = getContent();
  if (!content) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
        data-testid="legal-modal-overlay"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-3xl max-w-3xl w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
          data-testid="legal-modal-content"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-[#E8F5FA] to-[#FFF0EF]">
            <h2 className="text-2xl font-bold text-[#4A4A4A]">{content.title}</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-gray-100 transition-colors shadow-md"
              data-testid="legal-modal-close"
            >
              <X className="w-5 h-5 text-[#4A4A4A]" />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto p-6 flex-1">
            {content.isFaq ? (
              <div className="space-y-3">
                {faqData.map((item, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-2xl overflow-hidden"
                    data-testid={`faq-item-${index}`}
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-[#E8F5FA] transition-colors"
                      data-testid={`faq-question-${index}`}
                    >
                      <span className="font-semibold text-[#4A4A4A] pr-4">{item.question}</span>
                      <ChevronDown
                        className={`w-5 h-5 text-[#A8D8EA] flex-shrink-0 transition-transform duration-300 ${
                          openFaqIndex === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <AnimatePresence>
                      {openFaqIndex === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <p className="px-5 pb-5 text-gray-600 leading-relaxed">{item.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {content.sections.map((section, index) => (
                  <div key={index}>
                    {section.heading && (
                      <h3 className="text-lg font-bold text-[#7DBDD9] mb-2">{section.heading}</h3>
                    )}
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line">{section.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
